import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    User,
    UserExternalIdentifierOutputDTO,
} from 'app/core/user/user.types';
import {
    AddressDTO,
    CohabitingSituationDTO,
    EMERGENCY_CALL_WITHOUT_VOICE,
    GENDER,
    HealthInsuranceDTO,
    MARITAL_STATUS,
    MedicalUserDataDTO,
    MedicationType,
    ResidienceDTO,
    UserSettingsAppDTO,
} from 'app/modules/shared/models/user';
import { environment } from 'environments/environment';
import {
    catchError,
    map,
    Observable,
    of,
    ReplaySubject,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserService {
    private readonly baseUrl = environment.baseUrl;
    private readonly repoUrl = environment.repoUrl;

    private _httpClient = inject(HttpClient);
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);
    private _activeUser: ReplaySubject<User> = new ReplaySubject<User>(null);
    private _userSettings: ReplaySubject<UserSettingsAppDTO | null> =
        new ReplaySubject(null);
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User) {
        // Store the value
        this._user.next(value);
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    /**
     * Setter & getter for active user
     *
     * @param value
     */
    set activeUser(value: User) {
        // Store the value
        this._activeUser.next(value);
    }

    get activeUser$(): Observable<User> {
        return this._activeUser.asObservable();
    }

    /**
     * Getter for userSettings
     */
    get userSettings$(): Observable<UserSettingsAppDTO> {
        return this._userSettings.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Drops the user data held in memory (BSI TR-03161-2 O.Source_5): the replay subjects keep their last value,
     * so it is replaced by an empty one.
     */
    clear(): void {
        this._user.next(null);
        this._activeUser.next(null);
        this._userSettings.next(null);
    }

    /**
     * Get the current signed-in user data
     */
    getUserBase(): Observable<User> {
        return this._httpClient
            .get<User>(this.baseUrl + '/api/user/me/base-data')
            .pipe(
                tap((user) => {
                    user = this.mapUserData(user);
                    return user;
                }),
            );
        // .pipe(
        //     tap((user) => {
        //         user.active = true;
        //         this._user.next(user);
        //     })
        // );
    }

    getUser(userId: number): Observable<User> {
        return this._httpClient
            .get<User>(`${this.baseUrl}/api/user/${userId}/base-data`)
            .pipe(
                tap((user) => {
                    user = this.mapUserData(user);
                    return user;
                }),
            );
    }

    getActiveUser(): Observable<User> {
        return this.activeUser$.pipe(
            switchMap((user) =>
                this._httpClient
                    .get<User>(
                        `${this.baseUrl}/api/user/${user.userId}/base-data`,
                    )
                    .pipe(
                        tap((activeUser) => {
                            activeUser = this.mapUserData(activeUser);
                            // this._activeUser.next(person);
                            return activeUser;
                        }),
                    ),
            ),
        );
    }

    sendFCMToken(userId: number, fcm: string): Observable<boolean> {
        return this._httpClient.put<any>(
            `${this.baseUrl}/api/user/${userId}/firebase/token`,
            fcm,
        );
    }

    /**
     * Get the current signed-in user data
     */
    // get(): Observable<User> {
    //     return this._httpClient.get<User>('api/common/user').pipe(
    //         tap((user) => {
    //             this._user.next(user);
    //         })
    //     );
    // }

    /**
     * Update the user
     *
     * @param user
     */
    // update(user: User): Observable<any> {
    //     return this._httpClient.patch<User>('api/common/user', { user }).pipe(
    //         map((user) => {
    //             this._user.next(user);
    //         })
    //     );
    // }

    /**
     * Save User
     *
     * @param id
     * @param user
     */
    saveUser(user: User): Observable<User> {
        return this._httpClient
            .post(`${this.baseUrl}/api/user/${user.userId}/base-data/v2`, user)
            .pipe(
                take(1),
                tap((_user: User) => {
                    // Update the person if it's selected
                    // this._user.next(_user);
                    // Return the updated person
                    return _user;
                }),
            );
    }

    /**
     * delete the user
     *
     * @param user
     */
    delete(userId: number): Observable<any> {
        return this._httpClient.delete(`${this.repoUrl}/api/user/${userId}`);
    }

    sendMail(data): Observable<any> {
        return this.activeUser$.pipe(
            switchMap((user) =>
                this._httpClient
                    .post<any>(
                        `${this.baseUrl}/api/user/${user.userId}/email-with-additional-data`,
                        data,
                    )
                    .pipe(
                        catchError((error) => {
                            console.log(error);
                            return throwError(error);
                        }),
                    ),
            ),
        );
    }

    getExternalIdentifier(): Observable<UserExternalIdentifierOutputDTO> {
        return this.activeUser$.pipe(
            switchMap((user) =>
                this._httpClient.get<UserExternalIdentifierOutputDTO>(
                    `${this.baseUrl}/api/user/${user.userId}/external-identifier`,
                ),
            ),
        );
    }

    saveExternalIdentifier(externalId: string): Observable<any> {
        return this.activeUser$.pipe(
            switchMap((user) =>
                this._httpClient.post<any>(
                    `${this.baseUrl}/api/user/${user.userId}/external-identifier`,
                    { externalId },
                ),
            ),
        );
    }

    // User settings
    getLoginUserSettings(): Observable<UserSettingsAppDTO> {
        return this.user$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .get<UserSettingsAppDTO>(
                        `${this.baseUrl}/api/user/${user.userId}/user-settings`,
                    )
                    .pipe(
                        tap((userSettings) => {
                            userSettings.customConfig =
                                userSettings.customConfig
                                    ? JSON.parse(userSettings.customConfig)
                                    : '';
                            // this._userSettings.next(userSettings);
                            return userSettings;
                        }),
                        catchError((error) => {
                            if (error.status === 404) {
                                let defaultSetting = new UserSettingsAppDTO(
                                    null,
                                    false,
                                    false,
                                    true,
                                    MedicationType.CHECKBACK_GENERAL,
                                    null,
                                );
                                // this._userSettings.next(defaultSetting);
                                return of(defaultSetting);
                            }
                        }),
                    ),
            ),
        );
    }

    saveLoginUserSettings(
        userSettings: UserSettingsAppDTO,
    ): Observable<UserSettingsAppDTO> {
        if (typeof userSettings.customConfig !== 'string') {
            userSettings.customConfig = JSON.stringify(
                userSettings.customConfig,
            );
        }
        return this.user$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .post<UserSettingsAppDTO>(
                        `${this.baseUrl}/api/user/${user.userId}/user-settings`,
                        userSettings,
                    )
                    .pipe(
                        // tap((userSettings) => {
                        //     this._userSettings.next(userSettings);
                        // }),
                        catchError((error) => {
                            console.log(error);
                            return throwError(error);
                        }),
                    ),
            ),
        );
    }

    // User settings
    getUserSettings(): Observable<UserSettingsAppDTO> {
        return this.activeUser$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .get<UserSettingsAppDTO>(
                        `${this.baseUrl}/api/user/${user.userId}/user-settings`,
                    )
                    .pipe(
                        tap((userSettings) => {
                            userSettings.customConfig =
                                userSettings.customConfig
                                    ? JSON.parse(userSettings.customConfig)
                                    : '';
                            this._userSettings.next(userSettings);
                            return userSettings;
                        }),
                        catchError((error) => {
                            if (error.status === 404) {
                                let defaultSetting = new UserSettingsAppDTO(
                                    null,
                                    false,
                                    false,
                                    true,
                                    MedicationType.CHECKBACK_GENERAL,
                                    null,
                                );
                                this._userSettings.next(defaultSetting);
                                return of(defaultSetting);
                            }
                        }),
                    ),
            ),
        );
    }

    

    saveUserSettings(
        userSettings: UserSettingsAppDTO,
    ): Observable<UserSettingsAppDTO> {
        if (typeof userSettings.customConfig !== 'string') {
            userSettings.customConfig = JSON.stringify(
                userSettings.customConfig,
            );
        }
        return this.activeUser$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .post<UserSettingsAppDTO>(
                        `${this.baseUrl}/api/user/${user.userId}/user-settings`,
                        userSettings,
                    )
                    .pipe(
                        tap((userSettings) => {
                            this._userSettings.next(userSettings);
                        }),
                        catchError((error) => {
                            console.log(error);
                            return throwError(error);
                        }),
                    ),
            ),
        );
    }

    mapUserData(_person: User) {
        let person = new User();
        person = _person;
        person.salutation = _person.salutation || '-';
        // data["ADD_PROFILE_PIC"] = ""
        person.sex =
            !_person.sex || _person.sex === GENDER.UNKNOWN
                ? GENDER.UNKNOWN
                : _person.sex;
        person.birthday = _person.birthday;

        person.maritalStatus =
            !_person.maritalStatus ||
            _person.maritalStatus === MARITAL_STATUS.UNKNOWN
                ? MARITAL_STATUS.UNKNOWN
                : _person.maritalStatus;

        person.firstLanguage = _person.firstLanguage || 'german';
        if (!person.address) {
            person.address = new AddressDTO();
        }
        // data["POSTAL_CODE"] = userData.address?.zip
        // data["STREET_NO"] = userData.address?.streetNumber
        // data["STREET"] =  userData.address?.street
        // data["CITY"] = userData.address?.city
        // data["ADDRESS_SUFFIX"] = userData.address?.addressSuffix
        // data["DISTRICT"] = userData.address?.district
        // data["REGION"] = userData.address?.region
        if (!person.residence) {
            person.residence = new ResidienceDTO();
        }
        // data["COUNTRY"] = userData.address?.country
        // data["TYPE_OF_BUILDING"] = userData.residence?.type
        // data["LOC_OF_APART"] = userData.residence?.access
        // data["SPECIAL_FEATURE_OF_APART"] = userData.residence?.notes
        // data["PLACE_OF_MEDICATION"] = userData.medicalUserData?.medicationLocation

        if (!person.cohabitingSituation) {
            person.cohabitingSituation = new CohabitingSituationDTO();
        }

        // data["KEY_ON_SITE"] = userData.residence?.keySite
        // data["KEY_PIN"] = userData.residence?.keyNumber

        person.actionForEmergencyCallWithoutVoice =
            _person.actionForEmergencyCallWithoutVoice ||
            EMERGENCY_CALL_WITHOUT_VOICE.CALL_AMBULANCE;
        // data["INTEREST_AND_HOBBIES"] = userData.interests
        // data["REMARKS"] = userData.notesSocialEnvironment

        if (!person.medicalUserData) {
            person.medicalUserData = new MedicalUserDataDTO();
        }

        if (!person.medicalUserData?.healthInsurance) {
            person.medicalUserData.healthInsurance = new HealthInsuranceDTO();
        }
        return person;
    }
}
